import { useMemo, useState, type ReactNode } from 'react'
import { Icon } from '@iconify/react'
import { onlyDigits } from '@/components/CpfCnpjInput'
import type { LandingLuckyNumber } from '@/lib/landing-auth'
import {
  formatPurchaseAmount,
  groupLuckyNumbersByPurchase,
  type LuckyNumberPurchase,
} from './landing-profile-purchases'
import styles from './LandingNumbersPage.module.css'

type LandingProfileNumbersProps = {
  donationOnly: boolean
  query: string
  numbers: LandingLuckyNumber[]
  onQueryChange: (value: string) => void
  buyAction: ReactNode
}

function purchaseDate(value: string | null): { date: string; time: string } {
  if (!value) return { date: 'Data indisponível', time: '' }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return { date: 'Data indisponível', time: '' }
  return {
    date: new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(date),
    time: new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(
      date
    ),
  }
}

function NumberPills({ numbers }: { numbers: LandingLuckyNumber[] }) {
  return (
    <div className={styles.purchaseNumbers}>
      {numbers.map((item) => (
        <span className={styles.purchaseNumber} key={item.formatted_number}>
          {item.formatted_number}
        </span>
      ))}
    </div>
  )
}

function PurchaseCard({
  purchase,
  searchActive,
}: {
  purchase: LuckyNumberPurchase
  searchActive: boolean
}) {
  const { date, time } = purchaseDate(purchase.purchasedAt)
  const count = purchase.numbers.length
  const visibleCount = purchase.visibleNumbers.length
  const canExpand = visibleCount > 3

  return (
    <article className={styles.purchaseCard}>
      <div className={styles.purchaseHeader}>
        <div className={styles.purchaseIcon} aria-hidden>
          <Icon icon="mdi:ticket-confirmation-outline" />
        </div>
        <div className={styles.purchaseHeading}>
          <span className={styles.purchaseEyebrow}>
            {purchase.isWon
              ? ['scratch_card', 'purchase_scratch_card'].includes(purchase.prizeSource ?? '')
                ? 'Números ganhos na raspadinha'
                : purchase.prizeSource === 'achievement'
                  ? 'Números ganhos por selo'
                  : 'Números ganhos na roleta'
              : 'Compra confirmada'}
          </span>
          <time
            dateTime={purchase.purchasedAt ?? undefined}
            className={styles.purchaseDate}
          >
            {date}
            {time ? ` · ${time}` : ''}
          </time>
        </div>
        <div className={styles.purchaseAmount}>
          <span>{purchase.isWon ? 'Prêmio entregue' : 'Valor da compra'}</span>
          <strong>
            {purchase.isWon ? 'Sem custo' : formatPurchaseAmount(purchase.amount)}
          </strong>
        </div>
      </div>

      <div className={styles.purchaseBody}>
        <div className={styles.purchaseCount}>
          <span>
            {count} {count === 1 ? 'número' : 'números'}{' '}
            {purchase.isWon
              ? ['scratch_card', 'purchase_scratch_card'].includes(purchase.prizeSource ?? '')
                ? 'ganhos na raspadinha'
                : purchase.prizeSource === 'achievement'
                  ? 'ganhos por selo'
                  : 'ganhos na roleta'
              : 'nesta compra'}
          </span>
          {searchActive && visibleCount !== count ? (
            <small>
              {visibleCount} encontrado{visibleCount === 1 ? '' : 's'}
            </small>
          ) : null}
        </div>
        {canExpand ? (
          <details className={styles.purchaseDetails}>
            <summary>
              <NumberPills numbers={purchase.visibleNumbers.slice(0, 3)} />
              <span className={styles.purchaseExpand}>
                Ver todos os {visibleCount} números{' '}
                <Icon icon="mdi:chevron-down" aria-hidden />
              </span>
            </summary>
            <NumberPills numbers={purchase.visibleNumbers} />
          </details>
        ) : (
          <NumberPills numbers={purchase.visibleNumbers} />
        )}
      </div>
    </article>
  )
}

export function LandingProfileNumbers({
  donationOnly,
  query,
  numbers,
  onQueryChange,
  buyAction,
}: LandingProfileNumbersProps) {
  const [visibleCount, setVisibleCount] = useState(6)
  const allPurchases = useMemo(() => groupLuckyNumbersByPurchase(numbers, ''), [numbers])
  const purchases = useMemo(
    () => (query ? groupLuckyNumbersByPurchase(numbers, query) : allPurchases),
    [allPurchases, numbers, query]
  )

  return (
    <section className={styles.stack} aria-labelledby="landing-profile-numbers-title">
      <div>
        <h2 id="landing-profile-numbers-title" className={styles.sectionTitle}>
          {donationOnly ? 'Minhas compras nesta campanha' : 'Números da sorte'}
        </h2>
        <p className={styles.sectionText}>
          {donationOnly
            ? 'As compras nesta campanha não geram números da sorte. Aqui você acompanha seu perfil e histórico de participação.'
            : 'Compras e prêmios desta campanha, em um só lugar.'}
        </p>
      </div>

      {!donationOnly && numbers.length > 0 ? (
        <div className={styles.numbersOverview}>
          <Icon icon="mdi:ticket-percent-outline" aria-hidden />
          <strong>{numbers.length}</strong>
          <span>
            {allPurchases.some((group) => group.isWon) ? (
              'Números da Sorte'
            ) : (
              <>
                {numbers.length === 1 ? 'número' : 'números'} em {allPurchases.length}{' '}
                {allPurchases.length === 1 ? 'compra' : 'compras'}
              </>
            )}
          </span>
        </div>
      ) : null}

      {numbers.length > 0 ? (
        <div className={styles.numbersToolbar}>
          <label className={`${styles.control} ${styles.searchField}`}>
            <Icon icon="mdi:magnify" className={styles.controlIcon} aria-hidden />
            <input
              className={styles.input}
              type="search"
              inputMode="numeric"
              value={query}
              onChange={(event) =>
                onQueryChange(onlyDigits(event.target.value).slice(0, 32))
              }
              placeholder="Buscar número"
              aria-label="Buscar número"
            />
          </label>
        </div>
      ) : null}

      {purchases.length > 0 ? (
        <div className={styles.purchaseList}>
          {purchases.slice(0, visibleCount).map((purchase) => (
            <PurchaseCard
              key={purchase.key}
              purchase={purchase}
              searchActive={Boolean(query)}
            />
          ))}
          {visibleCount < purchases.length ? (
            <button
              className={styles.purchaseLoadMore}
              type="button"
              onClick={() => setVisibleCount((current) => current + 6)}
            >
              Ver mais compras <Icon icon="mdi:chevron-down" aria-hidden />
            </button>
          ) : null}
        </div>
      ) : (
        <p className={styles.emptyWell}>
          {query
            ? 'Nenhum número encontrado com esse filtro.'
            : donationOnly
              ? 'Esta campanha não atribui números da sorte às compras.'
              : 'Você ainda não tem números nesta campanha.'}
        </p>
      )}

      <div className={styles.actions}>{buyAction}</div>
    </section>
  )
}
